import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, RefreshCw, ArrowRightLeft, Save, History, Clock4, Download, ShieldCheck, KeyRound } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

function SecurityCard() {
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    api.get("/security/settings").then(({ data }) => {
      setEnabled(!!data.twofa_enabled);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);
  const toggle = async (v) => {
    setSaving(true);
    try {
      const { data } = await api.put("/security/settings", { twofa_enabled: v });
      setEnabled(!!data.twofa_enabled);
      toast.success(v
        ? "2FA is now REQUIRED for everyone — users without an authenticator will be asked to set one up at their next login"
        : "2FA requirement turned off — users who already enrolled keep their personal 2FA");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };
  return (
    <Card className="border-[#E8E8E6] max-w-3xl" data-testid="security-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <KeyRound className="h-5 w-5 text-[#40916C]" /> Security — two-factor authentication
        </CardTitle>
        <CardDescription>
          When on, every user must confirm login with a 6-digit code from an authenticator app
          (Google Authenticator, Authy, Microsoft Authenticator…). A verified device is trusted for
          7 days before the code is asked again. Accounts also lock for 15 minutes after 3 wrong passwords.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between rounded-lg border border-[#E8E8E6] bg-[#F8F8F5] p-4">
          <div>
            <p className="font-medium text-[#1C1F1E]">Require 2FA for this organisation</p>
            <p className="text-xs text-[#7A8A84] mt-0.5">
              {enabled ? "ON — all users must use an authenticator app" : "OFF — users can still enable 2FA individually from their profile"}
            </p>
          </div>
          <Switch checked={enabled} disabled={!loaded || saving} onCheckedChange={toggle} data-testid="org-twofa-toggle" />
        </div>
      </CardContent>
    </Card>
  );
}

const TIMEZONE_PRESETS = [
  { value: "Africa/Nairobi",   label: "Africa/Nairobi (EAT, UTC+3) — Kenya, Tanzania, Uganda" },
  { value: "Africa/Kampala",   label: "Africa/Kampala (EAT, UTC+3) — Uganda" },
  { value: "Africa/Dar_es_Salaam", label: "Africa/Dar es Salaam (EAT, UTC+3) — Tanzania" },
  { value: "Africa/Addis_Ababa", label: "Africa/Addis Ababa (EAT, UTC+3) — Ethiopia" },
  { value: "Africa/Kigali",    label: "Africa/Kigali (CAT, UTC+2) — Rwanda" },
  { value: "Africa/Lagos",     label: "Africa/Lagos (WAT, UTC+1) — Nigeria" },
  { value: "Africa/Johannesburg", label: "Africa/Johannesburg (SAST, UTC+2) — South Africa" },
  { value: "Africa/Cairo",     label: "Africa/Cairo (EET, UTC+2) — Egypt" },
  { value: "UTC",              label: "UTC (no offset)" },
  { value: "Europe/London",    label: "Europe/London (GMT/BST)" },
  { value: "America/New_York", label: "America/New York (ET)" },
  { value: "America/Los_Angeles", label: "America/Los Angeles (PT)" },
];

/**
 * Admin-only settings page. Currently surfaces the USD→KES exchange rate used
 * when rolling up mixed-currency invoices on the finance dashboard and when
 * printing invoice PDFs. Existing invoices keep their historically stamped
 * rate — a new rate only applies to invoices created/regenerated afterwards.
 */
export default function OrgSettings() {
  const navigate = useNavigate();
  const { organization, refreshOrg } = useAuth();
  const [rate, setRate] = useState(null);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // Phase 44d — timezone settings
  const [tzDraft, setTzDraft] = useState("");
  const [tzCustom, setTzCustom] = useState(false);
  const [tzSaving, setTzSaving] = useState(false);
  // Phase 44e — backup zip download
  const [downloading, setDownloading] = useState(false);
  // Phase 69 — agency booking date-shift margin
  const [marginDraft, setMarginDraft] = useState("2");
  const [marginSaved, setMarginSaved] = useState(2);
  const [marginSaving, setMarginSaving] = useState(false);
  useEffect(() => {
    api.get("/housing/agency-booking-settings").then(({ data }) => {
      setMarginDraft(String(data.date_margin_days ?? 2));
      setMarginSaved(data.date_margin_days ?? 2);
    }).catch(() => {});
  }, []);
  const saveMargin = async () => {
    const v = Number(marginDraft);
    if (Number.isNaN(v) || v < 0 || v > 14) { toast.error("Margin must be between 0 and 14 days"); return; }
    setMarginSaving(true);
    try {
      const { data } = await api.put(`/housing/agency-booking-settings?date_margin_days=${v}`);
      setMarginSaved(data.date_margin_days);
      toast.success(`Agencies can now shift dates by ±${data.date_margin_days} day(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setMarginSaving(false); }
  };

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/org/backup", { responseType: "blob", timeout: 120000 });
      const today = new Date().toISOString().slice(0, 10);
      const safeName = (organization?.name || "org").replace(/[^a-z0-9_-]/gi, "_");
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `missionhospitality-backup-${safeName}-${today}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Backup downloaded — ${(res.data.size / 1024).toFixed(0)} KB`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Backup download failed");
    } finally { setDownloading(false); }
  };

  useEffect(() => {
    if (organization?.timezone) {
      setTzDraft(organization.timezone);
      setTzCustom(!TIMEZONE_PRESETS.some((t) => t.value === organization.timezone));
    }
  }, [organization?.timezone]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/housing/exchange-rate");
      setRate(data);
      setDraft(String(data.usd_to_kes));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const v = Number(draft);
    if (!v || v <= 0 || v > 100000) {
      toast.error("Rate must be between 0 and 100,000");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put(`/housing/exchange-rate?usd_to_kes=${v}`);
      setRate(data);
      setDraft(String(data.usd_to_kes));
      toast.success(`Exchange rate updated · USD 1 = KES ${v}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const preview = Number(draft);
  const previewValid = preview > 0 && preview <= 100000;

  const saveTimezone = async () => {
    const tz = (tzDraft || "").trim();
    if (!tz) { toast.error("Pick a timezone"); return; }
    // Quick client-side validity check
    try { new Date().toLocaleDateString("en-CA", { timeZone: tz }); }
    catch { toast.error("Unknown IANA timezone"); return; }
    setTzSaving(true);
    try {
      await api.put("/org/me", { timezone: tz });
      toast.success(`Organisation timezone updated to ${tz}`);
      refreshOrg && refreshOrg();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setTzSaving(false); }
  };

  // Compute "now" in the selected tz so the admin can sanity-check the value
  const tzPreview = (() => {
    try {
      const now = new Date();
      const date = now.toLocaleDateString("en-CA", { timeZone: tzDraft || "UTC", weekday: "short", year: "numeric", month: "short", day: "numeric" });
      const time = now.toLocaleTimeString(undefined, { timeZone: tzDraft || "UTC", hour: "numeric", minute: "2-digit" });
      return `${date} · ${time}`;
    } catch { return "—"; }
  })();

  return (
    <div className="space-y-6 fade-in" data-testid="org-settings-page">
      <Button variant="ghost" onClick={() => navigate(-1)} className="text-[#525C58]" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back
      </Button>

      <div>
        <h1 className="text-4xl font-semibold tracking-tight" style={{ fontFamily: "Manrope, sans-serif" }}>
          Organisation settings
        </h1>
        <p className="text-[#525C58] mt-1">Currency, rate conversions and other org-wide preferences.</p>
      </div>

      <Card className="border-[#E8E8E6] max-w-3xl" data-testid="exchange-rate-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="h-5 w-5 text-[#40916C]" /> USD → KES exchange rate
          </CardTitle>
          <CardDescription>
            Used to roll up mixed-currency finance totals and to print the KES equivalent on USD invoice PDFs.
            Each invoice stamps the rate at the moment it is issued, so historical records are never silently restated.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {loading ? (
            <p className="text-sm text-[#7A8A84]">Loading…</p>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="rounded-lg border border-[#E8E8E6] bg-[#F8F8F5] p-4">
                  <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Current rate</p>
                  <p className="text-3xl font-bold text-[#1B4332] mt-1 tabular-nums" data-testid="current-rate-display">
                    {Number(rate?.usd_to_kes || 0).toFixed(2)}
                  </p>
                  <p className="text-xs text-[#7A8A84] mt-1">KES per 1 USD</p>
                  {rate?.is_default ? (
                    <Badge className="mt-3 bg-amber-100 text-amber-900 border border-amber-300 text-[10px]">DEFAULT — never set</Badge>
                  ) : (
                    <p className="text-[11px] text-[#525C58] mt-3 flex items-center gap-1">
                      <History className="h-3 w-3" />
                      Updated {rate?.updated_at ? new Date(rate.updated_at).toLocaleString() : "—"} by {rate?.updated_by || "—"}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="rate-input">New rate (KES per USD)</Label>
                  <Input
                    id="rate-input"
                    type="number"
                    step="0.01"
                    min="0"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    className="tabular-nums text-lg font-semibold"
                    data-testid="rate-input"
                  />
                  <p className="text-[11px] text-[#7A8A84]">
                    Preview: USD 100 = KES{" "}
                    <span className="font-semibold text-[#1C1F1E] tabular-nums" data-testid="rate-preview">
                      {previewValid ? (preview * 100).toLocaleString(undefined, { maximumFractionDigits: 2 }) : "—"}
                    </span>
                  </p>
                  <div className="flex gap-2 pt-2">
                    <Button
                      onClick={save}
                      disabled={saving || !previewValid || String(draft) === String(rate?.usd_to_kes)}
                      className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                      data-testid="save-rate-btn"
                    >
                      <Save className="h-4 w-4 mr-2" /> {saving ? "Saving…" : "Save rate"}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={load}
                      className="border-[#E8E8E6]"
                      data-testid="refresh-rate-btn"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>

              <div className="rounded-lg border border-[#E8E8E6] bg-white p-4 text-sm text-[#525C58] leading-relaxed">
                <p className="font-medium text-[#1C1F1E] mb-1">How this rate is applied</p>
                <ul className="list-disc pl-5 space-y-1 text-[13px]">
                  <li>New invoices stamp the current rate when they are created or regenerated.</li>
                  <li>Changing the rate does NOT retroactively change existing invoices — their stamped rate is preserved.</li>
                  <li>The finance dashboard converts each invoice&apos;s USD total using its own stamped rate.</li>
                  <li>Invoice PDFs with USD line items print a “≈ KES” conversion under the totals.</li>
                </ul>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Phase 44d — Organisation timezone */}
      <Card className="border-[#E8E8E6] max-w-3xl" data-testid="timezone-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock4 className="h-5 w-5 text-[#40916C]" /> Organisation timezone
          </CardTitle>
          <CardDescription>
            "Today" in the SPA — default booking dates, default expense dates, the helper-roster date picker — is computed in this zone, regardless of where the user's phone or browser is. Useful when staff travel.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-[#E8E8E6] bg-[#F8F8F5] p-4">
              <p className="text-[10px] uppercase tracking-wider text-[#7A8A84] font-semibold">Current setting</p>
              <p className="text-2xl font-bold text-[#1B4332] mt-1" data-testid="current-tz-display">
                {organization?.timezone || "Africa/Nairobi"}
              </p>
              <p className="text-xs text-[#7A8A84] mt-1">Now in this zone: <span className="font-mono">{tzPreview}</span></p>
            </div>
            <div className="space-y-2">
              <Label>Pick a timezone</Label>
              <Select
                value={tzCustom ? "__custom__" : tzDraft}
                onValueChange={(v) => {
                  if (v === "__custom__") setTzCustom(true);
                  else { setTzCustom(false); setTzDraft(v); }
                }}
              >
                <SelectTrigger data-testid="tz-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONE_PRESETS.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                  <SelectItem value="__custom__">Custom IANA timezone…</SelectItem>
                </SelectContent>
              </Select>
              {tzCustom && (
                <Input
                  value={tzDraft}
                  onChange={(e) => setTzDraft(e.target.value)}
                  placeholder="e.g. Pacific/Auckland"
                  className="font-mono text-sm"
                  data-testid="tz-custom-input"
                />
              )}
              <div className="flex gap-2 pt-1">
                <Button
                  onClick={saveTimezone}
                  disabled={tzSaving || !tzDraft || tzDraft === organization?.timezone}
                  className="bg-[#1B4332] hover:bg-[#2D6A4F]"
                  data-testid="save-tz-btn"
                >
                  <Save className="h-4 w-4 mr-2" /> {tzSaving ? "Saving…" : "Save timezone"}
                </Button>
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[#7A8A84]">
            This is a UI-only setting — historical timestamps stay in UTC and aren't modified. Refresh the page after saving so all default-date fields pick up the new zone.
          </p>
        </CardContent>
      </Card>

      {/* Phase 69 — agency booking adjustments */}
      <Card className="border-[#E8E8E6] max-w-3xl" data-testid="agency-margin-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="h-5 w-5 text-[#40916C]" /> Agency booking adjustments
          </CardTitle>
          <CardDescription>
            Sending agencies (and anyone who initiated a booking) can update flight/transport details and shift the stay
            dates by up to this many days. Every change is flagged "revised by" and needs a hospitality acknowledgement.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div className="space-y-1">
            <Label>Date shift margin (days)</Label>
            <Input type="number" min={0} max={14} value={marginDraft}
                   onChange={(e) => setMarginDraft(e.target.value)}
                   className="w-32" data-testid="agency-margin-input" />
          </div>
          <Button onClick={saveMargin} disabled={marginSaving || Number(marginDraft) === marginSaved}
                  className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="save-agency-margin-btn">
            <Save className="h-4 w-4 mr-2" /> {marginSaving ? "Saving…" : "Save margin"}
          </Button>
          <p className="text-xs text-[#7A8A84] pb-2">Currently ±{marginSaved} day(s). Set 0 to disable date changes entirely.</p>
        </CardContent>
      </Card>

      {/* Security — two-factor authentication */}
      <SecurityCard />

      {/* Phase 44e — Self-service backup */}
      <Card className="border-[#E8E8E6] max-w-3xl" data-testid="backup-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <ShieldCheck className="h-5 w-5 text-[#40916C]" /> Backup &amp; export
          </CardTitle>
          <CardDescription>
            Download every record + receipt photo for your organisation as a single zip. Keep at least one copy off this server (Google Drive, USB, S3) so a fire or hardware loss doesn't take both. Recommended: monthly minimum, weekly during peak season.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4 flex items-start gap-3">
            <Download className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0 text-sm">
              <p className="font-medium text-[#1B4332]">What's inside the zip</p>
              <ul className="text-xs text-[#525C58] mt-1 list-disc ml-4 space-y-0.5">
                <li>Every booking, lease, invoice, payment, expense, staff record, helper task and audit log</li>
                <li>Original receipt photos uploaded via "Scan receipt"</li>
                <li>Email templates, roles &amp; permissions, custom auto-rules</li>
                <li>A <code className="font-mono text-[10px]">manifest.json</code> + plain-text README with restore instructions</li>
              </ul>
              <p className="text-[11px] text-[#7A8A84] mt-2">PDF invoices and PDF receipts are generated on-the-fly so are NOT included — they can be regenerated at any time from the data inside the zip.</p>
            </div>
          </div>
          <Button
            onClick={downloadBackup}
            disabled={downloading}
            className="bg-[#1B4332] hover:bg-[#2D6A4F]"
            data-testid="download-backup-btn"
          >
            <Download className="h-4 w-4 mr-2" /> {downloading ? "Preparing zip…" : "Download backup zip"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
